import './publication-dialog.css';

import { cloud, type CloudPublication } from '../app/cloud';
import { el } from './dom';
import { activateModal } from './modal';

const ARTIST_MARKDOWN_LIMIT = 4000;

export interface PublicationDialogOptions {
  documentId: string;
  documentName: string;
  /** Persist the editor's current graph before creating its public snapshot. */
  prepare?(): Promise<void>;
  status(message: string): void;
  onPublished?(publication: CloudPublication): void;
}

/** Publish or republish a document and edit its mutable gallery description. */
export function openPublicationDialog(options: PublicationDialogOptions): () => void {
  document.querySelector('.publication-overlay')?.remove();

  let current: CloudPublication | null = null;
  let artistMarkdown = '';
  let accepted = false;
  let loading = true;
  let loadError = '';
  let busy = false;
  let error = '';
  let published: CloudPublication | null = null;
  let deactivate = (): void => {};

  const panel = el('section', {
    class: 'publication-dialog',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'publication-title',
    tabindex: '-1',
  });
  const overlay = el('div', { class: 'publication-overlay' }, [panel]);

  const close = (): void => {
    if (busy) return;
    deactivate();
    overlay.remove();
  };

  const header = (title: string, copy: string): HTMLElement =>
    el('header', { class: 'publication-header' }, [
      el('div', {}, [el('h1', { id: 'publication-title' }, [title]), el('p', {}, [copy])]),
      el(
        'button',
        {
          class: 'publication-close',
          type: 'button',
          'aria-label': 'Close publishing dialog',
          disabled: busy,
          onclick: close,
        },
        ['×'],
      ),
    ]);

  const render = (): void => {
    if (loading) {
      panel.replaceChildren(
        header('Publish to gallery', `Loading publication settings for “${options.documentName}”…`),
        el('p', { class: 'publication-loading', role: 'status' }, [
          'Checking the current gallery listing…',
        ]),
      );
      return;
    }

    if (loadError) {
      panel.replaceChildren(
        header('Publish to gallery', `Could not load settings for “${options.documentName}”.`),
        el('p', { class: 'publication-error publication-load-error', role: 'alert' }, [loadError]),
        el('div', { class: 'publication-actions' }, [
          el('button', { class: 'btn', type: 'button', onclick: close }, ['Cancel']),
          el(
            'button',
            {
              class: 'btn primary',
              type: 'button',
              onclick: () => void loadPublication(),
            },
            ['Try again'],
          ),
        ]),
      );
      return;
    }

    if (published) {
      panel.replaceChildren(
        header(
          'Published to gallery',
          `“${published.name}” revision ${published.revision} is live.`,
        ),
        el('div', { class: 'publication-success', role: 'status' }, [
          el('strong', {}, ['Your material is public.']),
          el('p', {}, [
            'You can keep editing its About text from the gallery page. Republish after graph changes to add another immutable revision.',
          ]),
        ]),
        el('div', { class: 'publication-actions' }, [
          el('button', { class: 'btn', type: 'button', onclick: close }, ['Done']),
          el(
            'a',
            {
              class: 'btn primary publication-gallery-link',
              href: `/gallery/${encodeURIComponent(published.id)}`,
              target: '_blank',
              rel: 'noreferrer',
            },
            ['View in gallery'],
          ),
        ]),
      );
      return;
    }

    const action = current && !current.unpublished ? 'Republish' : 'Publish';
    const characterCount = [...artistMarkdown].length;
    const tooLong = characterCount > ARTIST_MARKDOWN_LIMIT;
    const textarea = el('textarea', {
      class: 'publication-notes',
      rows: '9',
      placeholder:
        'Describe the material, useful parameters, scale, intended use, or anything another artist should know…',
      'aria-label': 'About this material in Markdown',
      'aria-describedby': 'publication-notes-help publication-character-count',
      disabled: busy,
    }) as HTMLTextAreaElement;
    textarea.value = artistMarkdown;

    const count = el(
      'span',
      {
        class: `publication-character-count${tooLong ? ' invalid' : ''}`,
        id: 'publication-character-count',
        'aria-live': 'polite',
      },
      [`${characterCount.toLocaleString()} / ${ARTIST_MARKDOWN_LIMIT.toLocaleString()}`],
    );
    const consent = el('input', {
      type: 'checkbox',
      checked: accepted,
      disabled: busy || current?.moderated === true,
    }) as HTMLInputElement;
    const submit = el(
      'button',
      {
        class: 'btn primary',
        type: 'submit',
        disabled: busy || !accepted || tooLong || current?.moderated === true,
      },
      [busy ? `${action}ing…` : `${action} to gallery`],
    ) as HTMLButtonElement;

    textarea.oninput = () => {
      artistMarkdown = textarea.value;
      const length = [...artistMarkdown].length;
      const invalid = length > ARTIST_MARKDOWN_LIMIT;
      count.textContent = `${length.toLocaleString()} / ${ARTIST_MARKDOWN_LIMIT.toLocaleString()}`;
      count.classList.toggle('invalid', invalid);
      submit.disabled = busy || !accepted || invalid || current?.moderated === true;
    };
    consent.onchange = () => {
      accepted = consent.checked;
      submit.disabled =
        busy ||
        !accepted ||
        [...artistMarkdown].length > ARTIST_MARKDOWN_LIMIT ||
        current?.moderated === true;
    };

    const form = el('form', { class: 'publication-form' }, [
      el('div', { class: 'publication-field' }, [
        el('div', { class: 'publication-label-line' }, [
          el('label', {}, ['About this material ', el('span', {}, ['optional'])]),
          count,
        ]),
        textarea,
        el('p', { class: 'publication-help', id: 'publication-notes-help' }, [
          'Markdown is supported. This text stays editable and is listing metadata—not part of the CC0 snapshot.',
        ]),
      ]),
      el('div', { class: 'publication-license' }, [
        el('label', {}, [
          consent,
          el('span', {}, [
            'I have the rights to publish this material and understand that its graph, title, and preview will be dedicated under CC0 1.0. The dedication cannot be revoked by unpublishing.',
          ]),
        ]),
        el('p', {}, [
          'Others may copy, modify, distribute, sell, and use the published snapshot without copyright restriction. ',
          el('a', { href: '/terms#publishing', target: '_blank', rel: 'noreferrer' }, [
            'Review publishing terms',
          ]),
          '.',
        ]),
      ]),
      current?.moderated
        ? el('p', { class: 'publication-error', role: 'alert' }, [
            'This listing is moderated and cannot be republished from the studio. Contact support if you believe this is a mistake.',
          ])
        : error
          ? el('p', { class: 'publication-error', role: 'alert' }, [error])
          : el('span'),
      el('div', { class: 'publication-actions' }, [
        el('button', { class: 'btn', type: 'button', disabled: busy, onclick: close }, ['Cancel']),
        submit,
      ]),
    ]);

    form.onsubmit = (event) => {
      event.preventDefault();
      if (submit.disabled) return;
      artistMarkdown = textarea.value;
      busy = true;
      error = '';
      render();
      void (async () => {
        await options.prepare?.();
        const result = await cloud.publishCc0(options.documentId, artistMarkdown);
        published = result;
        current = result;
        options.onPublished?.(result);
        options.status(
          `${action}ed “${result.name}” revision ${result.revision} to the public gallery`,
        );
      })()
        .catch((reason) => {
          error = (reason as Error).message;
          options.status(error);
        })
        .finally(() => {
          busy = false;
          render();
        });
    };

    panel.replaceChildren(
      header(
        `${action} to gallery`,
        `${action} an immutable snapshot of “${options.documentName}” and add public artist notes.`,
      ),
      form,
    );
  };

  async function loadPublication(): Promise<void> {
    loading = true;
    loadError = '';
    render();
    try {
      const publication = await cloud.publication(options.documentId);
      current = publication;
      artistMarkdown = publication?.artistMarkdown ?? '';
    } catch (reason) {
      loadError = (reason as Error).message;
    } finally {
      loading = false;
      render();
      window.setTimeout(() => panel.querySelector<HTMLTextAreaElement>('textarea')?.focus());
    }
  }

  overlay.addEventListener('pointerdown', (event) => {
    if (event.target === overlay) close();
  });
  document.body.appendChild(overlay);
  deactivate = activateModal(overlay, panel, { onEscape: close });
  void loadPublication();

  return close;
}
